import { z } from 'zod';
import {
  LIGHTWEIGHT_AUTOMATION_TEMPLATE_VARIABLES,
  renderLightweightAutomationDeliveryBody,
  type JsonObject,
  type JsonValue,
  type LightweightAutomationDeliveryBodyContext,
} from '../../../../../packages/shared-types/src/lightweight-automation.js';
import { TENANT_APP_USER_ROLE_KEYS } from '../../../../../packages/shared-types/src/tenant-app-user-roles.js';
import {
  createDefaultAutomationRecord,
  formatSecretRefUriInput,
  parseSecretRefUriInput,
  type AutomationOutputMode,
  type AutomationStatus,
  type LightweightAutomationRecord,
  resolveInternalAutomationModuleKey,
} from './automation-domain';

export const statusOptions = ['draft', 'active', 'paused'] as const satisfies readonly AutomationStatus[];
export const outputModeOptions = ['none', 'api_batch', 'webhook', 'message'] as const satisfies readonly AutomationOutputMode[];
export const apiBatchBodyShapeOptions = ['automation.result.v1', 'json_template.v1'] as const;
export const webhookBodyShapeOptions = ['automation.webhook.v1', 'json_template.v1'] as const;
export const scheduleKindOptions = ['cron', 'every', 'at'] as const;
export const executionModeOptions = ['manual', 'scheduled'] as const;
export const executionClassOptions = ['chat_only', 'approved_actions', 'operator_maintenance'] as const;
export const digestDestinationOptions = ['inbox', 'email', 'channel'] as const;
export const inputSourceKindOptions = ['none', 'static_list', 'http', 'webhook', 'connector'] as const;
export const httpMethodOptions = ['GET', 'POST'] as const;
export const iterationKindOptions = ['single', 'for_each'] as const;
export const ownerModeOptions = ['self', 'shared'] as const;

export const DEFAULT_API_BATCH_TEMPLATE = '{\n  "items": "{{result.payload.items}}"\n}';
export const DEFAULT_WEBHOOK_TEMPLATE = '{\n  "event": "automation.completed",\n  "summary": "{{result.summary}}",\n  "payload": "{{result.payload}}"\n}';

const optionalPositiveInt = z.preprocess(
  (value) => {
    if (value === '' || value === null || value === undefined) {
      return undefined;
    }
    return value;
  },
  z.coerce.number().int().positive().optional(),
);

export function createLightweightAutomationFormSchema(params: {
  canAuthorShared: boolean;
}) {
  return z.object({
    automationId: z.string().min(1, 'Automation ID is required'),
    name: z.string().min(1, 'Name is required'),
    agentId: z.string().min(1, 'Agent is required'),
    enabled: z.boolean().default(true),
    status: z.enum(statusOptions).default('draft'),
    ownerMode: z.enum(ownerModeOptions).default('self'),
    sharedUserIds: z.array(z.string()).default([]),
    sharedRoleKeys: z.array(z.enum(TENANT_APP_USER_ROLE_KEYS)).default([]),
    templateRef: z.string().default(''),
    moduleKey: z.string().default(''),
    instructionsMarkdown: z.string().min(1, 'Instructions are required'),
    inputSourceKind: z.enum(inputSourceKindOptions).default('none'),
    staticListItemsJson: z.string().default('[]'),
    httpUrl: z.string().default(''),
    httpMethod: z.enum(httpMethodOptions).default('GET'),
    httpHeadersJson: z.string().default('{}'),
    httpBodyJson: z.string().default('{}'),
    httpItemsPath: z.string().default('items'),
    httpLimit: optionalPositiveInt,
    webhookItemsPath: z.string().default('items'),
    webhookLimit: optionalPositiveInt,
    connectorToolName: z.string().default(''),
    connectorInputJson: z.string().default('{}'),
    connectorItemsPath: z.string().default('items'),
    connectorLimit: optionalPositiveInt,
    iterationKind: z.enum(iterationKindOptions).default('single'),
    maxItemsPerRun: optionalPositiveInt,
    concurrency: optionalPositiveInt,
    continueOnError: z.boolean().default(false),
    aggregateResults: z.boolean().default(true),
    deliveryMode: z.enum(outputModeOptions).default('message'),
    apiBatchUrl: z.string().default(''),
    apiBatchSecretRef: z.string().default(''),
    apiBatchHeaders: z.string().default('{}'),
    apiBatchTimeoutMs: z.coerce.number().int().positive().default(30000),
    apiBatchBodyShape: z.enum(apiBatchBodyShapeOptions).default('automation.result.v1'),
    apiBatchBodyTemplate: z.string().default(DEFAULT_API_BATCH_TEMPLATE),
    webhookUrl: z.string().default(''),
    webhookSecretRef: z.string().default(''),
    webhookHeaders: z.string().default('{}'),
    webhookTimeoutMs: z.coerce.number().int().positive().default(30000),
    webhookBodyShape: z.enum(webhookBodyShapeOptions).default('automation.webhook.v1'),
    webhookBodyTemplate: z.string().default(DEFAULT_WEBHOOK_TEMPLATE),
    digestDestination: z.enum(digestDestinationOptions).default('inbox'),
    digestTargetRef: z.string().default(''),
    executionMode: z.enum(executionModeOptions).default('manual'),
    executionClass: z.enum(executionClassOptions).default('operator_maintenance'),
    scheduleKind: z.enum(scheduleKindOptions).default('cron'),
    scheduleCron: z.string().default('0 8 * * *'),
    scheduleTz: z.string().default('UTC'),
    scheduleEveryMs: optionalPositiveInt,
    scheduleAt: z.string().default(''),
  }).superRefine((values, ctx) => {
    if (values.inputSourceKind === 'static_list') {
      try {
        const parsed = JSON.parse(values.staticListItemsJson.trim() || '[]') as unknown;
        if (!Array.isArray(parsed)) {
          throw new Error('Static list must be a JSON array');
        }
      } catch (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['staticListItemsJson'],
          message: error instanceof Error ? error.message : 'Static list must be valid JSON',
        });
      }
    }
    if (values.inputSourceKind === 'http' && !values.httpUrl.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['httpUrl'],
        message: 'HTTP URL is required',
      });
    }
    if (values.inputSourceKind === 'http' && values.httpHeadersJson.trim()) {
      try {
        const parsed = JSON.parse(values.httpHeadersJson.trim()) as unknown;
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('HTTP headers must be a JSON object');
        }
      } catch (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['httpHeadersJson'],
          message: error instanceof Error ? error.message : 'HTTP headers must be valid JSON',
        });
      }
    }
    if (values.inputSourceKind === 'http' && values.httpMethod === 'POST' && values.httpBodyJson.trim()) {
      try {
        JSON.parse(values.httpBodyJson.trim());
      } catch (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['httpBodyJson'],
          message: error instanceof Error ? error.message : 'HTTP body must be valid JSON',
        });
      }
    }
    if (values.inputSourceKind === 'connector' && !values.connectorToolName.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['connectorToolName'],
        message: 'Connector tool name is required',
      });
    }
    if (values.inputSourceKind === 'connector' && values.connectorInputJson.trim()) {
      try {
        const parsed = JSON.parse(values.connectorInputJson.trim()) as unknown;
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('Connector input must be a JSON object');
        }
      } catch (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['connectorInputJson'],
          message: error instanceof Error ? error.message : 'Connector input must be valid JSON',
        });
      }
    }
    if (values.inputSourceKind === 'webhook' && values.executionMode === 'scheduled') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['executionMode'],
        message: 'Webhook-triggered inputs cannot be scheduled.',
      });
    }
    if (values.ownerMode === 'shared' && !params.canAuthorShared) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ownerMode'],
        message: 'Only operators can author shared automations.',
      });
    }
    if (
      values.ownerMode === 'shared' &&
      values.sharedUserIds.length === 0 &&
      values.sharedRoleKeys.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sharedUserIds'],
        message: 'Select at least one user or tenant role for a shared automation.',
      });
    }
    if (values.deliveryMode === 'api_batch' && !values.apiBatchUrl.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['apiBatchUrl'], message: 'API batch URL is required' });
    }
    if (values.deliveryMode === 'webhook' && !values.webhookUrl.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['webhookUrl'], message: 'Webhook URL is required' });
    }
    if (values.deliveryMode === 'api_batch' && values.apiBatchBodyShape === 'json_template.v1') {
      try {
        renderLightweightAutomationDeliveryBody({
          bodyShape: values.apiBatchBodyShape,
          bodyTemplate: values.apiBatchBodyTemplate,
          context: buildPreviewDeliveryContext(values, 'api_batch'),
        });
      } catch (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['apiBatchBodyTemplate'],
          message: error instanceof Error ? error.message : 'Invalid API batch body template',
        });
      }
    }
    if (values.deliveryMode === 'webhook' && values.webhookBodyShape === 'json_template.v1') {
      try {
        renderLightweightAutomationDeliveryBody({
          bodyShape: values.webhookBodyShape,
          bodyTemplate: values.webhookBodyTemplate,
          context: buildPreviewDeliveryContext(values, 'webhook'),
        });
      } catch (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['webhookBodyTemplate'],
          message: error instanceof Error ? error.message : 'Invalid webhook body template',
        });
      }
    }
    for (const [path, raw] of [['apiBatchHeaders', values.apiBatchHeaders], ['webhookHeaders', values.webhookHeaders]] as const) {
      try {
        parseHeaders(raw);
      } catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: 'Headers must be valid JSON' });
      }
    }
    if (values.executionMode === 'scheduled' && values.scheduleKind === 'cron' && !values.scheduleCron.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scheduleCron'], message: 'Cron expression is required' });
    }
    if (values.executionMode === 'scheduled' && values.scheduleKind === 'every' && !values.scheduleEveryMs) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scheduleEveryMs'], message: 'Interval is required' });
    }
    if (values.executionMode === 'scheduled' && values.scheduleKind === 'at' && !values.scheduleAt.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scheduleAt'], message: 'Run-at time is required' });
    }
  });
}

export type LightweightAutomationFormValues = z.infer<
  ReturnType<typeof createLightweightAutomationFormSchema>
>;

export function createLightweightAutomationFormValues(
  tenantId: string,
  automationId?: string,
): LightweightAutomationFormValues {
  return toFormValues(createDefaultAutomationRecord(tenantId, automationId));
}

export function toFormValues(record: LightweightAutomationRecord): LightweightAutomationFormValues {
  return {
    automationId: record.automationId,
    name: record.name,
    agentId: record.agentId,
    enabled: record.enabled,
    status: record.status ?? 'draft',
    ownerMode: record.ownerMode,
    sharedUserIds: record.inboxRecipients?.userIds ?? [],
    sharedRoleKeys: record.inboxRecipients?.roleKeys ?? [],
    templateRef: record.templateRef ?? '',
    moduleKey: record.moduleKey,
    instructionsMarkdown: record.instructionsMarkdown,
    inputSourceKind: record.inputSource.kind,
    staticListItemsJson:
      record.inputSource.kind === 'static_list'
        ? JSON.stringify(record.inputSource.items, null, 2)
        : '[]',
    httpUrl: record.inputSource.kind === 'http' ? record.inputSource.url : '',
    httpMethod: record.inputSource.kind === 'http' ? record.inputSource.method ?? 'GET' : 'GET',
    httpHeadersJson:
      record.inputSource.kind === 'http' && record.inputSource.headers
        ? JSON.stringify(record.inputSource.headers, null, 2)
        : '{}',
    httpBodyJson:
      record.inputSource.kind === 'http' && record.inputSource.body !== undefined
        ? JSON.stringify(record.inputSource.body, null, 2)
        : '{}',
    httpItemsPath: record.inputSource.kind === 'http' ? record.inputSource.itemsPath ?? 'items' : 'items',
    httpLimit: record.inputSource.kind === 'http' ? record.inputSource.limit : undefined,
    webhookItemsPath:
      record.inputSource.kind === 'webhook' ? record.inputSource.itemsPath ?? 'items' : 'items',
    webhookLimit: record.inputSource.kind === 'webhook' ? record.inputSource.limit : undefined,
    connectorToolName:
      record.inputSource.kind === 'connector' ? record.inputSource.toolName : '',
    connectorInputJson:
      record.inputSource.kind === 'connector' && record.inputSource.input
        ? JSON.stringify(record.inputSource.input, null, 2)
        : '{}',
    connectorItemsPath:
      record.inputSource.kind === 'connector' ? record.inputSource.itemsPath ?? 'items' : 'items',
    connectorLimit:
      record.inputSource.kind === 'connector' ? record.inputSource.limit : undefined,
    iterationKind: record.iterationPolicy.kind,
    maxItemsPerRun: record.iterationPolicy.maxItemsPerRun,
    concurrency: record.iterationPolicy.concurrency,
    continueOnError: record.iterationPolicy.continueOnError ?? false,
    aggregateResults: record.iterationPolicy.aggregateResults,
    deliveryMode: record.delivery.mode,
    apiBatchUrl: record.delivery.apiBatch?.url ?? '',
    apiBatchSecretRef: formatSecretRefUriInput(record.delivery.apiBatch?.authTokenSecretRef),
    apiBatchHeaders: record.delivery.apiBatch?.headers ? JSON.stringify(record.delivery.apiBatch.headers, null, 2) : '{}',
    apiBatchTimeoutMs: record.delivery.apiBatch?.timeoutMs ?? 30000,
    apiBatchBodyShape: record.delivery.apiBatch?.bodyShape ?? 'automation.result.v1',
    apiBatchBodyTemplate: record.delivery.apiBatch?.bodyTemplate ?? DEFAULT_API_BATCH_TEMPLATE,
    webhookUrl: record.delivery.webhook?.url ?? '',
    webhookSecretRef: formatSecretRefUriInput(record.delivery.webhook?.authTokenSecretRef),
    webhookHeaders: record.delivery.webhook?.headers ? JSON.stringify(record.delivery.webhook.headers, null, 2) : '{}',
    webhookTimeoutMs: record.delivery.webhook?.timeoutMs ?? 30000,
    webhookBodyShape: record.delivery.webhook?.bodyShape ?? 'automation.webhook.v1',
    webhookBodyTemplate: record.delivery.webhook?.bodyTemplate ?? DEFAULT_WEBHOOK_TEMPLATE,
    digestDestination: record.delivery.message?.destination ?? 'inbox',
    digestTargetRef: record.delivery.message?.targetRef ?? '',
    executionMode: record.execution.mode,
    executionClass: record.execution.executionClass,
    scheduleKind: record.execution.schedule?.kind ?? 'cron',
    scheduleCron: record.execution.schedule?.cron ?? '0 8 * * *',
    scheduleTz: record.execution.schedule?.tz ?? 'UTC',
    scheduleEveryMs: record.execution.schedule?.everyMs,
    scheduleAt: record.execution.schedule?.at ?? '',
  };
}

export function toRecord(
  tenantId: string,
  currentUserId: string,
  values: LightweightAutomationFormValues,
): LightweightAutomationRecord {
  const nowIso = new Date().toISOString();
  const internalModuleKey = resolveInternalAutomationModuleKey({
    preferredModuleKey: values.moduleKey,
    inputSourceKind: values.inputSourceKind,
  });
  return {
    tenantId,
    automationId: values.automationId.trim(),
    name: values.name.trim(),
    enabled: values.enabled,
    agentId: values.agentId.trim(),
    ownerMode: values.ownerMode,
    ownerUserId: currentUserId,
    ...(values.ownerMode === 'shared'
      ? {
          inboxRecipients: {
            ...(values.sharedUserIds.length > 0
              ? { sharedUserIds: undefined }
              : {}),
            ...(values.sharedUserIds.length > 0
              ? { userIds: [...new Set(values.sharedUserIds.map((value) => value.trim()).filter(Boolean))].toSorted() }
              : {}),
            ...(values.sharedRoleKeys.length > 0
              ? { roleKeys: [...new Set(values.sharedRoleKeys)].toSorted() }
              : {}),
          },
        }
      : {}),
    moduleKey: internalModuleKey,
    instructionsMarkdown: values.instructionsMarkdown.trim(),
    inputSource:
      values.inputSourceKind === 'static_list'
        ? {
            kind: 'static_list',
            items: parseJsonArray(values.staticListItemsJson, []),
          }
        : values.inputSourceKind === 'http'
          ? {
              kind: 'http',
              url: values.httpUrl.trim(),
              method: values.httpMethod,
              ...(parseJsonObject(values.httpHeadersJson) ? { headers: parseHeaders(values.httpHeadersJson) } : {}),
              ...(values.httpMethod === 'POST' && values.httpBodyJson.trim()
                ? { body: parseJsonValue(values.httpBodyJson) }
                : {}),
              ...(values.httpItemsPath.trim() ? { itemsPath: values.httpItemsPath.trim() } : {}),
              ...(values.httpLimit ? { limit: values.httpLimit } : {}),
            }
          : values.inputSourceKind === 'webhook'
            ? {
                kind: 'webhook',
                ...(values.webhookItemsPath.trim() ? { itemsPath: values.webhookItemsPath.trim() } : {}),
                ...(values.webhookLimit ? { limit: values.webhookLimit } : {}),
              }
          : values.inputSourceKind === 'connector'
            ? {
                kind: 'connector',
                toolName: values.connectorToolName.trim(),
                ...(parseJsonObject(values.connectorInputJson) ? { input: parseJsonObject(values.connectorInputJson) } : {}),
                ...(values.connectorItemsPath.trim() ? { itemsPath: values.connectorItemsPath.trim() } : {}),
                ...(values.connectorLimit ? { limit: values.connectorLimit } : {}),
              }
          : { kind: 'none' },
    iterationPolicy: {
      kind: values.iterationKind,
      aggregateResults: values.aggregateResults,
      ...(values.maxItemsPerRun ? { maxItemsPerRun: values.maxItemsPerRun } : {}),
      ...(values.concurrency ? { concurrency: values.concurrency } : {}),
      ...(values.continueOnError ? { continueOnError: true } : {}),
    },
    ...(values.templateRef.trim() ? { templateRef: values.templateRef.trim() } : {}),
    execution: {
      mode: values.executionMode,
      executionClass: values.executionClass,
      ...(values.executionMode === 'scheduled'
        ? {
            schedule:
              values.scheduleKind === 'cron'
                ? { kind: 'cron' as const, cron: values.scheduleCron.trim(), tz: values.scheduleTz.trim() || 'UTC' }
                : values.scheduleKind === 'every'
                  ? { kind: 'every' as const, everyMs: values.scheduleEveryMs ?? 60000, tz: values.scheduleTz.trim() || 'UTC' }
                  : { kind: 'at' as const, at: values.scheduleAt.trim(), tz: values.scheduleTz.trim() || 'UTC' },
          }
        : {}),
    },
    delivery:
      values.deliveryMode === 'api_batch'
        ? {
            mode: 'api_batch',
            apiBatch: {
              url: values.apiBatchUrl.trim(),
              headers: parseHeaders(values.apiBatchHeaders),
              timeoutMs: values.apiBatchTimeoutMs,
              bodyShape: values.apiBatchBodyShape,
              ...(values.apiBatchBodyShape === 'json_template.v1'
                ? { bodyTemplate: values.apiBatchBodyTemplate }
                : {}),
              ...(parseSecretRefUriInput(values.apiBatchSecretRef)
                ? { authTokenSecretRef: parseSecretRefUriInput(values.apiBatchSecretRef) }
                : {}),
            },
          }
        : values.deliveryMode === 'webhook'
          ? {
              mode: 'webhook',
              webhook: {
                url: values.webhookUrl.trim(),
                headers: parseHeaders(values.webhookHeaders),
                timeoutMs: values.webhookTimeoutMs,
                bodyShape: values.webhookBodyShape,
                ...(values.webhookBodyShape === 'json_template.v1'
                  ? { bodyTemplate: values.webhookBodyTemplate }
                  : {}),
                ...(parseSecretRefUriInput(values.webhookSecretRef)
                  ? { authTokenSecretRef: parseSecretRefUriInput(values.webhookSecretRef) }
                  : {}),
              },
            }
          : values.deliveryMode === 'message'
            ? {
                mode: 'message',
                message: {
                  destination: values.digestDestination,
                  ...(values.digestTargetRef.trim() ? { targetRef: values.digestTargetRef.trim() } : {}),
                },
              }
            : { mode: 'none' },
    status: values.status,
    updatedAt: nowIso,
    createdAt: nowIso,
  };
}

export function buildDeliveryPreview(values: LightweightAutomationFormValues): unknown {
  if (values.deliveryMode === 'api_batch') {
    return renderLightweightAutomationDeliveryBody({
      bodyShape: values.apiBatchBodyShape,
      bodyTemplate: values.apiBatchBodyTemplate,
      context: buildPreviewDeliveryContext(values, 'api_batch'),
    });
  }
  if (values.deliveryMode === 'webhook') {
    return renderLightweightAutomationDeliveryBody({
      bodyShape: values.webhookBodyShape,
      bodyTemplate: values.webhookBodyTemplate,
      context: buildPreviewDeliveryContext(values, 'webhook'),
    });
  }
  return { deliveryMode: values.deliveryMode };
}

export function flattenErrors(errors: Record<string, unknown>, prefix = ''): string[] {
  const results: string[] = [];
  for (const [key, value] of Object.entries(errors)) {
    if (!value || typeof value !== 'object') {
      continue;
    }
    const path = prefix ? `${prefix}.${key}` : key;
    if ('message' in value && typeof value.message === 'string') {
      results.push(value.message);
    }
    results.push(...flattenErrors(value as Record<string, unknown>, path));
  }
  return Array.from(new Set(results));
}

function buildPreviewDeliveryContext(
  values: LightweightAutomationFormValues,
  deliveryMode: 'api_batch' | 'webhook',
): LightweightAutomationDeliveryBodyContext {
  return {
    automationId: values.automationId,
    runId: 'preview_run',
    generatedAt: '2026-03-30T08:00:00.000Z',
    moduleKey: resolveInternalAutomationModuleKey({
      preferredModuleKey: values.moduleKey,
      inputSourceKind: values.inputSourceKind,
    }),
    deliveryMode,
    result: {
      summary: 'Example automation result.',
      payload: {
        items: [
          {
            title: 'Example result item',
            summary: 'Example summary for a structured automation payload.',
            url: 'https://example.com/item/1',
            date: '2026-03-30T07:30:00.000Z',
            label: 'example',
            topic: 'example-topic',
          },
        ],
      },
    },
  };
}

function parseHeaders(raw: string): Record<string, string> | undefined {
  const trimmed = raw.trim();
  if (!trimmed || trimmed === '{}') {
    return undefined;
  }
  const parsed = JSON.parse(trimmed) as unknown;
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Headers must be a JSON object');
  }
  const entries = Object.entries(parsed as Record<string, unknown>).filter(([, value]) => typeof value === 'string');
  return entries.length > 0 ? Object.fromEntries(entries as Array<[string, string]>) : undefined;
}

function parseJsonArray(raw: string, fallback: JsonValue[]): JsonValue[] {
  const trimmed = raw.trim();
  if (!trimmed) {
    return fallback;
  }
  const parsed = JSON.parse(trimmed) as unknown;
  return Array.isArray(parsed) ? (parsed as JsonValue[]) : fallback;
}

function parseJsonObject(raw: string): JsonObject | undefined {
  const trimmed = raw.trim();
  if (!trimmed || trimmed === '{}') {
    return undefined;
  }
  const parsed = JSON.parse(trimmed) as unknown;
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
    ? (parsed as JsonObject)
    : undefined;
}

function parseJsonValue(raw: string): JsonValue | undefined {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }
  return JSON.parse(trimmed) as JsonValue;
}

export { LIGHTWEIGHT_AUTOMATION_TEMPLATE_VARIABLES, AUTOMATION_MODULE_OPTIONS };
